import { type ReactNode, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Button,
  Calendar,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui';
import { X } from '@ui/icons';
import { cn } from '@shared/lib';
import { t } from '@shared/i18n';

/** Today at 00:00 (local) — the earliest selectable recurrence start date. */
const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export type RepeatFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type RepeatReminder = 'none' | 'sameDay' | 'oneDay' | 'twoDays' | 'oneWeek';

export interface RepeatConfig {
  frequency: RepeatFrequency;
  every: number;
  date: Date | undefined;
  endRepeat: 'never' | 'onDate';
  endDate?: Date | undefined;
  reminder: RepeatReminder;
}

export const DEFAULT_REPEAT: RepeatConfig = {
  frequency: 'yearly',
  every: 1,
  date: undefined,
  endRepeat: 'never',
  endDate: undefined,
  reminder: 'none',
};

export interface RepeatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value?: RepeatConfig | undefined;
  onSave: (config: RepeatConfig) => void;
}

/** A bordered card grouping rows (matches the Figma sections). */
function Card({ children }: { children: ReactNode }) {
  return <div className="overflow-hidden rounded-lg border border-border">{children}</div>;
}

/** A label-left / control-right row inside a Card. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-11.75 items-center justify-between gap-3 border-b border-divider px-4 py-2 last:border-b-0">
      <span className="text-sm font-medium text-fg">{label}</span>
      {children}
    </div>
  );
}

/** Borderless inline select (value + chevron) used on the right of a Row. */
function InlineSelect({
  value,
  onValueChange,
  ariaLabel,
  options,
}: {
  value: string;
  onValueChange: (value: string) => void;
  ariaLabel: string;
  options: { value: string; label: string }[];
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label={ariaLabel}
        className="h-auto w-auto gap-1.5 border-0 bg-transparent p-0 text-sm text-fg focus-visible:ring-0"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="z-65 w-full" viewportClassName="w-full p-0">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Form body — remounts on each open so it always starts from the current `initial` config. */
function RepeatForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: RepeatConfig;
  onSave: (config: RepeatConfig) => void;
  onCancel: () => void;
}) {
  const m = t.newBill.repeat;
  const [cfg, setCfg] = useState<RepeatConfig>(initial);

  const frequencyOptions = [
    { value: 'daily', label: m.frequencies.daily },
    { value: 'weekly', label: m.frequencies.weekly },
    { value: 'monthly', label: m.frequencies.monthly },
    { value: 'yearly', label: m.frequencies.yearly },
  ];
  const reminderOptions = [
    { value: 'none', label: m.reminders.none },
    { value: 'sameDay', label: m.reminders.sameDay },
    { value: 'oneDay', label: m.reminders.oneDay },
    { value: 'twoDays', label: m.reminders.twoDays },
    { value: 'oneWeek', label: m.reminders.oneWeek },
  ];

  return (
    <>
      <div className="flex flex-col gap-5 px-4 pb-2">
        <Card>
          <Row label={m.frequency}>
            <InlineSelect
              ariaLabel={m.frequency}
              value={cfg.frequency}
              onValueChange={(v) => setCfg({ ...cfg, frequency: v as RepeatFrequency })}
              options={frequencyOptions}
            />
          </Row>
          <Row label={m.every}>
            <Input
              type="number"
              min={1}
              aria-label={m.every}
              value={String(cfg.every)}
              onChange={(e) => setCfg({ ...cfg, every: Math.max(1, Number(e.target.value) || 1) })}
              className="h-9 w-14 rounded-[4px] bg-surface px-2 text-center text-sm"
            />
          </Row>
        </Card>

        <div className="rounded-lg border border-border p-4">
          <Calendar
            mode="single"
            selected={cfg.date}
            onSelect={(date) => setCfg({ ...cfg, date })}
            // A recurrence start can't be in the past — disable days before today.
            disabled={{ before: startOfToday() }}
            className="mx-auto w-full"
          />
        </div>

        <Card>
          <Row label={m.endRepeat}>
            <InlineSelect
              ariaLabel={m.endRepeat}
              value={cfg.endRepeat}
              onValueChange={(v) => setCfg({ ...cfg, endRepeat: v as 'never' | 'onDate' })}
              options={[
                { value: 'never', label: m.endNever },
                { value: 'onDate', label: m.endOnDate },
              ]}
            />
          </Row>
          {cfg.endRepeat === 'onDate' && (
            <div className="border-b border-divider p-4 bg-canvas/10">
              <Calendar
                mode="single"
                selected={cfg.endDate}
                onSelect={(date) => setCfg({ ...cfg, endDate: date })}
                // Recurrence end date can't be before start date
                disabled={cfg.date ? { before: cfg.date } : { before: startOfToday() }}
                className="mx-auto w-full"
              />
            </div>
          )}
          <Row label={m.earlyReminder}>
            <InlineSelect
              ariaLabel={m.earlyReminder}
              value={cfg.reminder}
              onValueChange={(v) => setCfg({ ...cfg, reminder: v as RepeatReminder })}
              options={reminderOptions}
            />
          </Row>
        </Card>
      </div>

      <div className="flex items-center gap-2.5 border-t border-border-soft bg-surface-raised px-5 py-3">
        <Button variant="primary" size="md" onClick={() => onSave(cfg)}>
          {t.newBill.save}
        </Button>
        <Button variant="secondary" size="md" onClick={onCancel}>
          {t.newBill.cancel}
        </Button>
      </div>
    </>
  );
}

/**
 * "Repeat" recurrence modal (Figma) — Frequency · Every · a date calendar · End Repeat · Early
 * Reminder, with Save/Cancel. Opened from the Date picker's "Repeat" action.
 */
export function RepeatModal({ open, onOpenChange, value, onSave }: RepeatModalProps) {
  const m = t.newBill.repeat;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-60 bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-60 flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-100 -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl bg-surface-raised shadow-overlay focus:outline-none',
          )}
        >
          {/* Header */}
          <div className="flex items-center gap-4 border-b border-border bg-header-bar px-5 py-2">
            <Dialog.Title className="flex-1 text-lg font-semibold text-fg-strong">
              {m.title}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t.newBill.close}
              className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-border-soft text-fg hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto pt-5">
            <RepeatForm
              initial={value ?? DEFAULT_REPEAT}
              onSave={(cfg) => {
                onSave(cfg);
                onOpenChange(false);
              }}
              onCancel={() => onOpenChange(false)}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
